import * as XLSX from 'xlsx';

/**
 * Parse an MCG Jazz Upload Template (.xlsx) file.
 * Returns { people: [], songs: [], concerts: [] }
 * Each array contains plain objects matching the sheet columns.
 * Empty sheets or missing sheets are returned as empty arrays.
 */
export function parseUploadFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const people = parseSheet(workbook, 'People', normalizePerson);
        const songs = parseSheet(workbook, 'Songs', normalizeSong);
        const concerts = parseSheet(workbook, 'Concerts', normalizeConcert);

        if (people.length === 0 && songs.length === 0 && concerts.length === 0) {
          reject(new Error('All sheets are empty. Please fill out at least one sheet.'));
          return;
        }

        resolve({ people, songs, concerts });
      } catch (err) {
        reject(new Error(`Failed to parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsArrayBuffer(file);
  });
}

function parseSheet(workbook, sheetName, normalizer) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];

  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return rows
    .map((row, idx) => normalizer(row, idx))
    .filter((row) => row !== null);
}

/** Check if a value looks like the italic example row */
function isExampleRow(values) {
  const joined = values.join(' ').toLowerCase();
  return (
    joined.includes('david murray') ||
    joined.includes("shakill's warrior") ||
    joined.includes('e.g.')
  );
}

function normalizePerson(row, idx) {
  const name = String(row.name ?? row.Name ?? '').trim();
  const instrument = String(row.instrument ?? row.Instrument ?? '').trim();
  const bio = String(row.bio ?? row.Bio ?? '').trim();

  if (isExampleRow([name, instrument, bio])) return null;
  if (!name) return null;

  return { _idx: idx, name, instrument, bio };
}

function normalizeSong(row, idx) {
  const title = String(row.title ?? row.Title ?? '').trim();
  const composer = String(row.composer ?? row.Composer ?? '').trim();

  if (isExampleRow([title, composer])) return null;
  if (!title) return null;

  return { _idx: idx, title, composer };
}

function normalizeConcert(row, idx) {
  const title = String(row.title ?? row.Title ?? '').trim();
  const rawDate = row.date ?? row.Date ?? '';
  const location = String(row.location ?? row.Location ?? '').trim();
  const description = String(row.description ?? row.Description ?? '').trim();
  const performers = String(row.performers ?? row.Performers ?? '').trim();
  const setlist = String(row.setlist ?? row.Setlist ?? '').trim();

  if (isExampleRow([title, String(rawDate), location, performers, setlist])) return null;
  if (!title) return null;

  const date = formatDate(rawDate);

  const parsedPerformers = performers
    ? performers.split(';').map((p) => {
        const parts = p.trim().split('-').map((s) => s.trim());
        return { name: parts[0] || '', instrument: parts[1] || '' };
      }).filter((p) => p.name)
    : [];

  const parsedSetlist = setlist
    ? setlist.split(';').map((s, i) => ({
        title: s.trim(),
        trackNumber: i + 1,
      })).filter((s) => s.title)
    : [];

  return {
    _idx: idx,
    title,
    date,
    location,
    description,
    performers: parsedPerformers,
    setlist: parsedSetlist,
  };
}

function formatDate(raw) {
  if (!raw) return '';
  if (typeof raw === 'number') {
    const d = XLSX.SSF.parse_date_code(raw);
    if (d) {
      return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
    }
  }
  const str = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return str;
}
